import { getCenter, getHeight, getWidth } from 'ol/extent';
import { Polygon } from 'ol/geom';
import { getPointResolution } from 'ol/proj';

/**
 * The example the user marks for "Find similar": a dragged box, or a click.
 *
 * The backend compares feature patches of about `patch_m` metres. An example has to sit on the
 * object: a box one patch off a plant (on the soil edge) finds plant edges rather than plants.
 * A box shorter than two patches can't be drawn that precisely, so it is taken as a click, and a
 * click becomes one patch centred on the click point.
 */

// Ground size of one feature patch until the backend has told us (`patch_m` in its answer)
export const DEFAULT_PATCH_M = 0.45;

/**
 * Ground metres per EPSG:3857 map unit at a point. Web Mercator stretches with latitude.
 * @param {number[]} point - [x, y] in EPSG:3857
 * @returns {number}
 */
export const metresPerMapUnit = point => getPointResolution('EPSG:3857', 1, point, 'm');

/**
 * The box to send for what the user drew.
 * @param {number[]} drawn - [minX, minY, maxX, maxY] in EPSG:3857; a click is a box of no size
 * @param {number} [patchM] - Ground size of one feature patch, in metres
 * @returns {{box: number[], isClick: boolean}} The box to send, and whether it stands for a click
 */
export const exampleBox = (drawn, patchM = DEFAULT_PATCH_M) => {
	const centre = getCenter(drawn);
	const perUnit = metresPerMapUnit(centre);
	const longerSide = Math.max(getWidth(drawn), getHeight(drawn)) * perUnit;
	if (longerSide >= 2 * patchM) return { box: drawn, isClick: false };

	const half = patchM / perUnit / 2;
	return {
		box: [centre[0] - half, centre[1] - half, centre[0] + half, centre[1] + half],
		isClick: true,
	};
};

/**
 * A box with rounded corners, for drawing: four quarter circles of five points each.
 * @param {number[]} extent - [minX, minY, maxX, maxY]
 * @param {number} radius - In map units; at most half the shorter side
 * @returns {Polygon}
 */
export const roundedBox = ([minX, minY, maxX, maxY], radius) => {
	const r = Math.min(radius, (maxX - minX) / 2, (maxY - minY) / 2);
	const centres = [
		[maxX - r, maxY - r],
		[minX + r, maxY - r],
		[minX + r, minY + r],
		[maxX - r, minY + r],
	];
	const ring = centres.flatMap(([x, y], corner) =>
		[0, 1, 2, 3, 4].map(step => {
			const angle = ((corner + step / 4) * Math.PI) / 2;
			return [x + r * Math.cos(angle), y + r * Math.sin(angle)];
		})
	);
	return new Polygon([[...ring, ring[0]]]);
};
